import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { JiraPkeyService } from '../../../core/services/jira-pkey.service';
import { JiraProjectKey } from '../../../core/models/jira-project-key';

// PrimeNG
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService, ConfirmationService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { JiraStatus } from '../../../core/enums/jira-status';
import { Subscription } from 'rxjs';
import { RegexConstants } from '../../../core/constants/regex-constants';



@Component({
  selector: 'em-jira-project-key',
  templateUrl: './jira-project-key.component.html',
  standalone: true,
  imports: [
    CommonModule, FormsModule, TableModule, ButtonModule, TagModule, 
    ToastModule, TranslateModule, ConfirmDialogModule, DialogModule, 
    InputTextModule, DropdownModule 
  ],
  providers: [MessageService, ConfirmationService]
})
export class JiraPkeyComponent implements OnInit, OnDestroy {
  jiraPkeys: JiraProjectKey[] = [];
  displayDialog: boolean = false;
  isEditMode: boolean = false;
  public readonly JiraStatus = JiraStatus;
  translations: any = {};
  
  statusOptions: { label: string; value: JiraStatus }[] = [];
  private langChangeSubscription?: Subscription;

  selectedPkey: JiraProjectKey = {
    pkey: '',
    status: JiraStatus.ACTIVE 
  };

  get isPkeyValid(): boolean {
    const pkey = this.selectedPkey.pkey?.trim();
    if (!pkey) return false;
    return RegexConstants.JIRA_PROJECT_KEY.test(pkey);
  }

  constructor(
    private jiraPkeyService: JiraPkeyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void { 
    this.loadData();
    this.initializeStatusOptions();
    this.loadTranslations();
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(() => {
      this.initializeStatusOptions();
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();
  }

  private initializeStatusOptions(): void {
    this.statusOptions = Object.values(JiraStatus).map(status => ({
      label: this.translateService.instant(`layout.configurations.jira.statuses.${status}`),
      value: status
    }));
  }

  private loadTranslations(): void {
    this.translateService
      .get([
        'layout.configurations.jira.deleteJiraKey.errorMessage.title',
        'layout.configurations.jira.deleteJiraKey.errorMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  loadData(): void {
    this.jiraPkeyService.getAll().subscribe({
      next: (data) => this.jiraPkeys = data,
      error: (err) => this.showError(err)
    });
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.selectedPkey = { pkey: '', status: JiraStatus.ACTIVE };
    this.displayDialog = true;
  }

  openEditModal(item: JiraProjectKey): void {
    this.isEditMode = true;
    this.selectedPkey = { ...item };
    this.displayDialog = true;
  }

  savePkey(): void {
    if (!this.isPkeyValid) return;

    const request = (this.isEditMode && this.selectedPkey.id)
      ? this.jiraPkeyService.update(this.selectedPkey.id, this.selectedPkey)
      : this.jiraPkeyService.create(this.selectedPkey);

    request.subscribe({
      next: () => this.handleSuccess(this.isEditMode ? 'updated' : 'created'),
      error: (err) => this.showError(err)
    });
  }

  onToggleStatus(id: number | undefined): void {
    if (!id) return;
    this.jiraPkeyService.toggleStatus(id).subscribe({
      next: () => {
        this.loadData();
        this.showSuccess('layout.configurations.jira.toast.statusUpdated');
      },
      error: (err) => this.showError(err)
    });
  }

  onDelete(event: Event, id: number | undefined): void {
    if (!id) return;
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translateService.instant('layout.configurations.jira.deleteConfirm.message'),
      header: this.translateService.instant('layout.configurations.jira.deleteConfirm.title'),
      icon: 'pi pi-info-circle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translateService.instant('layout.configurations.jira.deleteConfirm.yes'),
      rejectLabel: this.translateService.instant('layout.configurations.jira.deleteConfirm.no'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.jiraPkeyService.delete(id).subscribe({
          next: () => {
            this.loadData();
            this.showSuccess('layout.configurations.jira.toast.deleted');
          },
          error: (err) => this.showDeleteError(err)
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translateService.instant('layout.configurations.jira.deleteConfirm.cancelledSummary'),
          detail: this.translateService.instant('layout.configurations.jira.deleteConfirm.cancelledDetail'),
          life: 3000,
        });
      }
    });
  }

  private handleSuccess(action: 'created' | 'updated'): void {
    this.loadData();
    this.displayDialog = false;
    this.showSuccess(`layout.configurations.jira.toast.${action}`); // i18n-ignore (translation key)
  }

  private showSuccess(detailKey: string): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('layout.configurations.jira.toast.successTitle'),
      detail: this.translateService.instant(detailKey)
    });
  }

  private showError(err: any): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('layout.configurations.jira.toast.errorTitle'),
      detail: this.getErrorMessage(err)
    });
  }

  private showDeleteError(err: any): void {
    if (err?.status === 409) {
      this.messageService.add({
        severity: 'error',
        summary: this.getTranslatedMessage(
          'layout.configurations.jira.deleteJiraKey.errorMessage.title',
          this.translateService.instant('layout.configurations.jira.toast.errorTitle')
        ),
        detail: this.getTranslatedMessage(
          'layout.configurations.jira.deleteJiraKey.errorMessage.detail',
          this.translateService.instant('common.validationError')
        )
      });
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('layout.configurations.jira.toast.errorTitle'),
      detail: this.translateService.instant('common.validationError')
    });
  }

  private getTranslatedMessage(key: string, fallback: string): string {
    const translated = this.translations[key] || this.translateService.instant(key);
    return translated && translated !== key ? translated : fallback;
  }

  private getErrorMessage(err: any): string {
    const payload = err?.error;

    if (typeof payload === 'string') {
      return payload;
    }

    if (payload?.message) {
      return payload.message;
    }

    if (payload?.errorMessage) {
      return payload.errorMessage;
    }

    if (payload?.error) {
      return payload.error;
    }

    if (err?.message && !err.message.startsWith('Http failure response')) { // i18n-ignore (Angular error prefix)
      return err.message;
    }

    return this.translateService.instant('common.validationError');
  }
}
